import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Save, ArrowLeft, X, Plus, Loader2, Flame, Star, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CITIES = ["Lucknow", "Gurugram", "Noida", "Delhi", "Mumbai", "Bangalore", "Pune", "Hyderabad", "Dubai", "Singapore"];
const TYPES = ["apartment", "villa", "plot", "commercial"];
const STATUSES = ["ready", "under_construction", "new_launch"];
const CURRENCIES = ["INR", "AED", "SGD", "USD"];
const DEAL_LABELS = ["Festive Offer", "Pre-launch", "Limited time", "Special Allocation", "Developer Direct", "Pre-booking"];

export default function AdminProjectEdit() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newImg, setNewImg] = useState("");
  const [uploading, setUploading] = useState(false);

  const isAdmin = user && user !== false && user.role === "admin";

  useEffect(() => {
    if (user === false) return;
    if (user && !isAdmin) {
      navigate("/");
      return;
    }
    if (isAdmin) {
      api.get(`/projects/${id}`).then((r) => {
        setForm({
          ...r.data,
          bhk: r.data.bhk || [],
          amenities: r.data.amenities || [],
          images: r.data.images || [],
          discount_pct: r.data.discount_pct || "",
        });
      }).catch(() => toast.error("Project not found")).finally(() => setLoading(false));
    }
  }, [id, user, isAdmin, navigate]);

  const update = (key, value) => setForm({ ...form, [key]: value });

  const updateNumber = (key, value) => setForm({ ...form, [key]: value === "" ? null : Number(value) });

  const addImage = () => {
    if (!newImg.trim()) return;
    update("images", [...form.images, newImg.trim()]);
    setNewImg("");
  };

  const removeImage = (idx) => update("images", form.images.filter((_, i) => i !== idx));

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    let okCount = 0;
    const newUrls = [];
    try {
      for (const file of files) {
        try {
          const fd = new FormData();
          fd.append("file", file);
          const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          newUrls.push(r.data.url);
          okCount += 1;
        } catch {
          toast.error(`Failed: ${file.name}`);
        }
      }
      if (newUrls.length > 0) {
        update("images", [...form.images, ...newUrls]);
        toast.success(`${okCount} of ${files.length} image${files.length > 1 ? "s" : ""} uploaded`);
      }
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        developer: form.developer,
        city: form.city,
        locality: form.locality,
        type: form.type,
        status: form.status,
        bhk: form.bhk,
        price_min: Number(form.price_min) || 0,
        price_max: Number(form.price_max) || 0,
        price_per_sqft: Number(form.price_per_sqft) || 0,
        area_min: Number(form.area_min) || 0,
        area_max: Number(form.area_max) || 0,
        description: form.description,
        amenities: form.amenities,
        images: form.images,
        rera: form.rera || null,
        possession: form.possession || null,
        currency: form.currency,
        featured: !!form.featured,
        hot_deal: !!form.hot_deal,
        discount_pct: form.hot_deal && form.discount_pct ? Number(form.discount_pct) : null,
        deal_label: form.hot_deal ? form.deal_label : null,
      };
      await api.put(`/projects/${id}`, payload);
      toast.success("Saved successfully");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading...</div>;
  if (!form) return null;

  const Field = ({ label, children, span = "" }) => (
    <div className={span}>
      <label className="overline block mb-1.5">{label}</label>
      {children}
    </div>
  );

  const inputCls = "w-full bg-white border border-slate-200 text-slate-900 px-3 py-2.5 text-sm rounded-lg focus:outline-none focus:border-[#D4A437] focus:ring-2 focus:ring-amber-100 transition-all";

  return (
    <div data-testid="admin-edit-page" className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <Link to="/admin/projects" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#D4A437] mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to projects
      </Link>

      <form onSubmit={handleSave} className="space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="overline mb-2">Edit Project</div>
            <h1 className="font-serif text-3xl font-bold text-slate-900">{form.name}</h1>
            <p className="text-sm text-slate-500 mt-1">{form.developer} · {form.city}</p>
          </div>
          <div className="flex gap-3">
            <Link to={`/projects/${id}`} target="_blank" className="px-4 py-2.5 border border-slate-200 text-sm font-semibold text-slate-700 rounded-lg hover:bg-slate-50">Preview</Link>
            <button type="submit" disabled={saving} data-testid="admin-save-btn" className="inline-flex items-center gap-2 bg-[#D4A437] hover:bg-[#B88C2E] text-white font-semibold px-6 py-2.5 rounded-lg transition-colors disabled:opacity-50 shadow-sm">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        {/* Hot Deal panel — top priority for marketing */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Flame className="w-5 h-5 text-[#EF4444]" fill="currentColor" />
            <h2 className="font-serif text-xl font-bold text-slate-900">Promotion Settings</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <label className="flex items-start gap-3 cursor-pointer bg-white rounded-xl p-4 border border-amber-100 hover:border-[#D4A437] transition-colors">
              <input type="checkbox" data-testid="admin-featured-toggle" checked={!!form.featured} onChange={(e) => update("featured", e.target.checked)} className="mt-1 w-4 h-4 accent-[#D4A437]" />
              <div>
                <div className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-[#D4A437]" fill="currentColor" /> Featured</div>
                <div className="text-xs text-slate-500 mt-1">Show on home carousel</div>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer bg-white rounded-xl p-4 border border-amber-100 hover:border-[#EF4444] transition-colors">
              <input type="checkbox" data-testid="admin-hotdeal-toggle" checked={!!form.hot_deal} onChange={(e) => update("hot_deal", e.target.checked)} className="mt-1 w-4 h-4 accent-[#EF4444]" />
              <div>
                <div className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5"><Flame className="w-3.5 h-3.5 text-[#EF4444]" fill="currentColor" /> Hot Deal</div>
                <div className="text-xs text-slate-500 mt-1">Show on Hot Deals page</div>
              </div>
            </label>
            {form.hot_deal && (
              <div className="bg-white rounded-xl p-4 border border-amber-100">
                <div className="text-xs text-slate-500 mb-1">Discount %</div>
                <input type="number" min="0" max="50" data-testid="admin-discount-input" value={form.discount_pct ?? ""} onChange={(e) => update("discount_pct", e.target.value)} className={inputCls} />
              </div>
            )}
          </div>
          {form.hot_deal && (
            <div className="mt-4">
              <label className="overline block mb-1.5">Deal Label</label>
              <select data-testid="admin-deal-label" value={form.deal_label || ""} onChange={(e) => update("deal_label", e.target.value)} className={inputCls}>
                <option value="">— select —</option>
                {DEAL_LABELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          )}
        </div>

        {/* Basic info */}
        <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
          <h2 className="font-serif text-xl font-bold text-slate-900 mb-5">Basic Info</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Project Name" span="md:col-span-2"><input data-testid="admin-input-name" value={form.name || ""} onChange={(e) => update("name", e.target.value)} className={inputCls} required /></Field>
            <Field label="Developer"><input data-testid="admin-input-developer" value={form.developer || ""} onChange={(e) => update("developer", e.target.value)} className={inputCls} required /></Field>
            <Field label="City"><select data-testid="admin-input-city" value={form.city || ""} onChange={(e) => update("city", e.target.value)} className={inputCls}>{CITIES.map((c) => <option key={c}>{c}</option>)}</select></Field>
            <Field label="Locality" span="md:col-span-2"><input data-testid="admin-input-locality" value={form.locality || ""} onChange={(e) => update("locality", e.target.value)} className={inputCls} required /></Field>
            <Field label="Type"><select data-testid="admin-input-type" value={form.type || ""} onChange={(e) => update("type", e.target.value)} className={inputCls}>{TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
            <Field label="Status"><select data-testid="admin-input-status" value={form.status || ""} onChange={(e) => update("status", e.target.value)} className={inputCls}>{STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}</select></Field>
            <Field label="Possession"><input data-testid="admin-input-possession" value={form.possession || ""} onChange={(e) => update("possession", e.target.value)} placeholder="Ready / Dec 2026" className={inputCls} /></Field>
            <Field label="RERA No."><input data-testid="admin-input-rera" value={form.rera || ""} onChange={(e) => update("rera", e.target.value)} placeholder="Optional" className={inputCls} /></Field>
            <Field label="Description" span="md:col-span-3"><textarea data-testid="admin-input-description" value={form.description || ""} onChange={(e) => update("description", e.target.value)} rows={3} className={inputCls} /></Field>
          </div>
        </div>

        {/* Pricing */}
        <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
          <h2 className="font-serif text-xl font-bold text-slate-900 mb-5">Pricing &amp; Area</h2>
          <div className="grid md:grid-cols-4 gap-4">
            <Field label="Currency"><select data-testid="admin-input-currency" value={form.currency || "INR"} onChange={(e) => update("currency", e.target.value)} className={inputCls}>{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</select></Field>
            <Field label="Min Price"><input type="number" data-testid="admin-input-pricemin" value={form.price_min ?? ""} onChange={(e) => updateNumber("price_min", e.target.value)} className={inputCls} /></Field>
            <Field label="Max Price"><input type="number" data-testid="admin-input-pricemax" value={form.price_max ?? ""} onChange={(e) => updateNumber("price_max", e.target.value)} className={inputCls} /></Field>
            <Field label="Per Sqft"><input type="number" data-testid="admin-input-persqft" value={form.price_per_sqft ?? ""} onChange={(e) => updateNumber("price_per_sqft", e.target.value)} className={inputCls} /></Field>
            <Field label="Area Min (sqft)"><input type="number" data-testid="admin-input-areamin" value={form.area_min ?? ""} onChange={(e) => updateNumber("area_min", e.target.value)} className={inputCls} /></Field>
            <Field label="Area Max (sqft)"><input type="number" data-testid="admin-input-areamax" value={form.area_max ?? ""} onChange={(e) => updateNumber("area_max", e.target.value)} className={inputCls} /></Field>
            <Field label="BHK (comma)" span="md:col-span-2">
              <input
                data-testid="admin-input-bhk"
                value={(form.bhk || []).join(", ")}
                onChange={(e) => update("bhk", e.target.value.split(",").map(s => parseInt(s.trim(), 10)).filter(Boolean))}
                placeholder="2, 3, 4"
                className={inputCls}
              />
            </Field>
          </div>
        </div>

        {/* Images */}
        <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
          <h2 className="font-serif text-xl font-bold text-slate-900 mb-5">Images</h2>
          <p className="text-xs text-slate-500 mb-4">Paste a URL, upload one image, or select <strong>multiple files at once</strong> for bulk upload. First image becomes the hero/cover.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {form.images.map((img, i) => (
              <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200">
                <img src={img} alt="" className="w-full h-full object-cover" />
                {i === 0 && <div className="absolute top-1.5 left-1.5 bg-[#D4A437] text-white text-[9px] px-1.5 py-0.5 rounded font-bold">COVER</div>}
                <button type="button" onClick={() => removeImage(i)} data-testid={`admin-remove-img-${i}`} className="absolute top-1.5 right-1.5 w-6 h-6 bg-white/90 hover:bg-red-500 hover:text-white rounded-full flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <label className="aspect-square rounded-lg border-2 border-dashed border-slate-300 hover:border-[#D4A437] flex flex-col items-center justify-center text-slate-500 hover:text-[#D4A437] cursor-pointer transition-colors text-xs gap-1.5">
              <input type="file" accept="image/*" multiple onChange={handleFileUpload} className="hidden" data-testid="admin-img-upload-input" />
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              <span className="font-semibold">{uploading ? "Uploading…" : "Bulk Upload"}</span>
              <span className="text-[10px] text-slate-400">Pick 1 or many</span>
            </label>
          </div>
          <div className="flex gap-2">
            <input value={newImg} onChange={(e) => setNewImg(e.target.value)} placeholder="https://images.unsplash.com/..." data-testid="admin-img-url-input" className={inputCls} />
            <button type="button" onClick={addImage} data-testid="admin-img-add-btn" className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-semibold inline-flex items-center gap-2 whitespace-nowrap"><Plus className="w-4 h-4" /> Add URL</button>
          </div>
        </div>

        {/* Amenities */}
        <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
          <h2 className="font-serif text-xl font-bold text-slate-900 mb-3">Amenities</h2>
          <textarea
            data-testid="admin-input-amenities"
            value={(form.amenities || []).join(", ")}
            onChange={(e) => update("amenities", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
            rows={3}
            placeholder="Pool, Gym, Clubhouse, Concierge..."
            className={inputCls}
          />
          <p className="text-xs text-slate-500 mt-2">Comma-separated. Each becomes a chip on the project page.</p>
        </div>

        <div className="flex justify-end pt-4">
          <button type="submit" disabled={saving} data-testid="admin-save-btn-bottom" className="inline-flex items-center gap-2 bg-[#D4A437] hover:bg-[#B88C2E] text-white font-semibold px-8 py-3 rounded-lg transition-colors disabled:opacity-50 shadow-md">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
